import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Shield, Camera, ArrowRight, AlertCircle } from "lucide-react";
import { PasswordGate } from "../components/adresseingabe/PasswordGate";
import { IdScannerModal } from "../components/adresseingabe/IdScannerModal";
import { Modal } from "../components/adresseingabe/Modal";
import { DSGVO_TEXT } from "../components/adresseingabe/legalTexts";
import { HiddenResetButton } from "../components/adresseingabe/HiddenResetButton";
import { useFavicon } from "../hooks/useFavicon";

const TOKEN_KEY = "rk-adresseingabe-token";
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/adresseingabe`;

/**
 * /ausweis-nachreichen?d=<base64-token>
 *
 * Folge-Subseite, auf der ein Mandant ausschließlich seine
 * Ausweisdokumente nachreichen kann. Die Mandantendaten kommen aus dem
 * Base64-URL-Parameter (keine Server-Persistenz) und landen direkt im
 * fertigen PDF.
 */
export const AusweisNachreichen = () => {
  useFavicon("/favicon-ausweis.svg");
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const [decodeError, setDecodeError] = useState("");
  const [ausweis, setAusweis] = useState({
    ausweis_vorderseite: "",
    ausweis_rueckseite: "",
    ausweis2_vorderseite: "",
    ausweis2_rueckseite: "",
  });
  const [showScanner, setShowScanner] = useState(false);
  const [showDsgvo, setShowDsgvo] = useState(false);
  const [dsgvoOk, setDsgvoOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const urlToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("d") || "";
  }, []);

  useEffect(() => {
    document.title = "Ausweis nachreichen · Rechtsanwalt Keller";
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (t) setToken(t);
  }, []);

  // Decode token (server-side, so the secret stays out of the bundle)
  useEffect(() => {
    if (!token || !urlToken || data) return;
    (async () => {
      try {
        const { data: payload } = await axios.get(`${API}/ausweis/decode`, {
          params: { d: urlToken },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(payload);
      } catch (err) {
        setDecodeError(err.response?.data?.detail || "Ungültiger Link.");
      }
    })();
  }, [token, urlToken, data]);

  const onAuthSuccess = (t) => {
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const isEheleute = data?.an?.toLowerCase() === "eheleute";
  const isFirma = data?.an?.toLowerCase() === "firma";
  const mandantName = [data?.vn, data?.nn].filter(Boolean).join(" ").trim();
  const partnerName = data?.p2
    ? [data.p2.vn, data.p2.nn].filter(Boolean).join(" ").trim()
    : "";

  const persons = useMemo(() => {
    if (!data) return [];
    if (isFirma) return [];
    const out = [
      {
        key: "mandant",
        label: isEheleute ? `Person 1 — ${mandantName}` : mandantName || "Mandant",
        front: ausweis.ausweis_vorderseite,
        back: ausweis.ausweis_rueckseite,
      },
    ];
    if (isEheleute) {
      out.push({
        key: "partner",
        label: `Person 2 — ${partnerName || "Mandant"}`,
        front: ausweis.ausweis2_vorderseite,
        back: ausweis.ausweis2_rueckseite,
      });
    }
    return out;
  }, [data, isEheleute, isFirma, mandantName, partnerName, ausweis]);

  const allCaptured =
    persons.length > 0 && persons.every((p) => p.front && p.back);

  const submit = async () => {
    if (!dsgvoOk) {
      toast.error("Bitte Datenschutzerklärung bestätigen.");
      return;
    }
    if (!allCaptured) {
      toast.error("Bitte alle Ausweisseiten aufnehmen.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(
        `${API}/submit_ausweis`,
        {
          token: urlToken,
          ...ausweis,
          datenschutz_accepted: dsgvoOk,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.email_sent) {
        toast.success("Ausweis erfolgreich übermittelt.");
      } else {
        toast.message("Ausweis erfasst.", { description: res.reason });
      }
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Renderpfade ----------------------------------------------------

  if (!token) {
    return (
      <div className="min-h-screen bg-[#fafaf7] text-[#0a1128]">
        <PasswordGate
          onSuccess={onAuthSuccess}
          title="Ausweis nachreichen"
          subtitle="Diese Seite ist passwortgeschützt. Bitte geben Sie das Zugangs-Passwort ein, um Ihre Ausweisdokumente nachzureichen."
        />
        <HiddenResetButton
          rootPath={`/ausweis-nachreichen${window.location.search || ""}`}
          storageKeys={[TOKEN_KEY]}
        />
      </div>
    );
  }

  if (!urlToken) {
    return (
      <CenterCard testId="ausweis-no-token">
        <AlertCircle className="w-7 h-7 text-[#d90429] mb-3" />
        <h1 className="font-display font-bold text-[28px]">Ungültiger Aufruf.</h1>
        <p className="mt-3 text-[#505a74] text-[14px]">
          Diese Seite ist nur über den persönlichen Link aus der Bestätigungs-
          E-Mail erreichbar.
        </p>
      </CenterCard>
    );
  }

  if (decodeError) {
    return (
      <CenterCard testId="ausweis-decode-error">
        <AlertCircle className="w-7 h-7 text-[#d90429] mb-3" />
        <h1 className="font-display font-bold text-[28px]">Link ungültig.</h1>
        <p className="mt-3 text-[#505a74] text-[14px]">{decodeError}</p>
      </CenterCard>
    );
  }

  if (!data) {
    return (
      <CenterCard testId="ausweis-loading">
        <p className="text-[14px] text-[#505a74]">Lade Mandantendaten…</p>
      </CenterCard>
    );
  }

  if (done) {
    return (
      <CenterCard testId="ausweis-success">
        <div className="inline-flex w-16 h-16 rounded-full bg-[#0a1128] text-white items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-[#d90429]" />
        </div>
        <h1 className="font-display font-bold text-[36px] md:text-[44px] leading-[1.05] tracking-tight text-center">
          Vielen Dank.
        </h1>
        <p className="mt-5 text-[#505a74] text-[15px] leading-relaxed text-center max-w-md">
          Ihre Ausweisdokumente wurden sicher an die Kanzlei übermittelt.
        </p>
      </CenterCard>
    );
  }

  if (isFirma) {
    return (
      <CenterCard testId="ausweis-firma-skip">
        <Shield className="w-7 h-7 text-[#d90429] mb-3" />
        <h1 className="font-display font-bold text-[28px]">Kein Ausweis erforderlich.</h1>
        <p className="mt-3 text-[#505a74] text-[14px] text-center max-w-md">
          Für Firmen wird kein Personalausweis verlangt. Sollten Sie versehentlich
          auf diesen Link gelangt sein, können Sie das Fenster schließen.
        </p>
      </CenterCard>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf7] text-[#0a1128]">
      <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
        <div className="flex items-center gap-3 text-[#505a74] mb-5">
          <span className="section-mark" />
          <span className="eyebrow">Ausweis-Nachreichung</span>
        </div>
        <h1
          data-testid="ausweis-title"
          className="font-display font-bold text-[36px] md:text-[48px] leading-[1.0] tracking-tight"
        >
          Ausweis nachreichen.
        </h1>
        <p className="mt-5 text-[#505a74] text-[14px] leading-relaxed">
          Hallo <strong className="text-[#0a1128]">{mandantName}</strong>
          {isEheleute && partnerName ? <> &amp; <strong className="text-[#0a1128]">{partnerName}</strong></> : null}
          , gemäß <strong>§ 11 Geldwäschegesetz</strong> benötigen wir je ein
          Foto der Vorder- und Rückseite Ihres Personalausweises (oder
          Reisepasses). Ihre persönlichen Daten sind bereits hinterlegt — wir
          benötigen ausschließlich die Bilder.
        </p>

        <div className="mt-8 bg-white border border-[#e8e6df] rounded-sm p-6 md:p-7">
          <div className="text-[11px] font-mono text-[#d90429] tracking-widest">01</div>
          <h2 className="font-display text-[20px] font-bold tracking-tight mt-1 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#d90429]" /> Hinterlegte Daten
          </h2>
          <dl className="mt-5 text-[13px] grid sm:grid-cols-2 gap-x-6 gap-y-2">
            <Item k="Name" v={mandantName} />
            <Item k="Geburtsdatum" v={fmtDate(data.gd)} />
            <Item
              k="Anschrift"
              v={`${data.st || ""} ${data.hn || ""}, ${data.pz || ""} ${data.ot || ""}`}
            />
            {isEheleute && partnerName && <Item k="Eheleute — Person 2" v={partnerName} />}
            {data.em && <Item k="E-Mail" v={data.em} />}
          </dl>

          <div className="mt-8 pt-6 border-t border-[#e8e6df]">
            <div className="grid sm:grid-cols-2 gap-3">
              {persons.map((p) => (
                <div
                  key={p.key}
                  data-testid={`ausweis-tile-${p.key}`}
                  className="border border-[#e8e6df] rounded-sm p-4 bg-[#fafaf7]"
                >
                  <div className="text-[12px] font-semibold text-[#0a1128] mb-3">
                    {p.label}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Thumb label="Vorderseite" b64={p.front} />
                    <Thumb label="Rückseite" b64={p.back} />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowScanner(true)}
              data-testid="ausweis-open-scanner"
              className={`mt-5 px-5 py-3 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 ${
                allCaptured ? "btn-ghost" : "btn-primary"
              }`}
            >
              <Camera className="w-4 h-4" />
              {allCaptured ? "Ausweisaufnahmen ändern" : "Ausweis jetzt aufnehmen"}
            </button>
          </div>
        </div>

        <div className="mt-6 bg-white border border-[#e8e6df] rounded-sm p-6 md:p-7">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="ausweis-dsgvo-accept"
              checked={dsgvoOk}
              onChange={(e) => setDsgvoOk(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#d90429] shrink-0"
            />
            <span className="text-[14px] text-[#0a1128] leading-relaxed">
              <strong>Ich habe die{" "}
                <button
                  type="button"
                  data-testid="ausweis-dsgvo-open"
                  onClick={() => setShowDsgvo(true)}
                  className="link-underline text-[#d90429] hover:text-[#0a1128] transition font-semibold"
                >
                  Datenschutzerklärung
                </button>{" "}
                gelesen
              </strong>{" "}
              und bin mit der Verarbeitung meiner Daten einverstanden.
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={submitting || !dsgvoOk || !allCaptured}
          data-testid="ausweis-submit"
          className="mt-8 btn-primary px-7 py-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50"
        >
          {submitting ? "Wird gesendet…" : "Ausweis übermitteln"}
          <ArrowRight className="w-4 h-4" />
        </button>

        <Modal
          open={showDsgvo}
          title="Datenschutzerklärung"
          testIdPrefix="dsgvo"
          onClose={() => setShowDsgvo(false)}
          maxWidthClass="max-w-4xl"
        >
          <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-[#0a1128]">
            {DSGVO_TEXT}
          </div>
        </Modal>

        <IdScannerModal
          open={showScanner}
          onClose={() => setShowScanner(false)}
          persons={persons}
          onCapture={(personKey, side, b64) => {
            const front = side === "front";
            if (personKey === "mandant") {
              setAusweis((s) => ({
                ...s,
                [front ? "ausweis_vorderseite" : "ausweis_rueckseite"]: b64,
              }));
            } else if (personKey === "partner") {
              setAusweis((s) => ({
                ...s,
                [front ? "ausweis2_vorderseite" : "ausweis2_rueckseite"]: b64,
              }));
            }
          }}
        />
      </div>
      <HiddenResetButton
        rootPath={`/ausweis-nachreichen${window.location.search || ""}`}
        storageKeys={[TOKEN_KEY]}
      />
    </div>
  );
};

const Item = ({ k, v }) => (
  <>
    <dt className="text-[11px] uppercase tracking-wider text-[#505a74]">{k}</dt>
    <dd className="text-[#0a1128]">{v || "—"}</dd>
  </>
);

const Thumb = ({ label, b64 }) => (
  <div className="bg-white border border-[#e8e6df] rounded-sm overflow-hidden">
    <div className="text-[10px] font-semibold tracking-wider uppercase text-[#505a74] px-2 py-1 border-b border-[#e8e6df]">
      {label}
    </div>
    <div className="aspect-[1.586/1] bg-[#f4f3ed] flex items-center justify-center">
      {b64 ? (
        <img src={b64} alt={label} className="w-full h-full object-cover" />
      ) : (
        <span className="text-[11px] text-[#a8a89a]">— noch nicht aufgenommen —</span>
      )}
    </div>
  </div>
);

const CenterCard = ({ testId, children }) => (
  <div className="min-h-screen bg-[#fafaf7] text-[#0a1128] flex items-center justify-center px-6">
    <div data-testid={testId} className="max-w-md w-full text-center flex flex-col items-center">
      {children}
    </div>
    <HiddenResetButton
      rootPath={`/ausweis-nachreichen${window.location.search || ""}`}
      storageKeys={[TOKEN_KEY]}
    />
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

export default AusweisNachreichen;
